import React, { useCallback, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";

// region External libraries
import { SidebarDrawer } from "@npm-telluria-tecnologia/telluria-ui";
import { Badge } from "@mui/material";
// endregion External libraries
// region Assets
import LogotipoMinifiedWhite from "@assets/company/LogotipoMinifiedWhite.svg";
import LogoTopconCustomer from "@assets/company/TopconCustomer.svg";
// endregion Assets
// region Helpers
import utils from "@helpers/utils";
// endregion Helpers
// region Atoms
import { ExternalSvg, SelectClient } from "@atoms/index";
// endregion Atoms
// region Hooks
import { useWindowDimensions } from "@hooks/useWindowDimensions";
import { useAuth } from "@hooks/useAuth";
import { useSeenVersion } from "@hooks/useSeenVersion";
// endregion Hooks
// region Molecules
import { NavbarIcons, SidebarDrawerMenus, SidebarDrawerSecondaryMenus } from "@molecules/index";
// endregion Molecules
// region Libraries
import { Menu } from "@libraries/icons";
import { usePermissions } from "@pages/Office";
import { SidebarMenuProvider } from "@hooks/menuItensContext";
// endregion Libraries
// region Styles
import useTheme from "@styles/useTheme";
import packageJson from "../../../package.json";
import * as Styled from "./styles";
// endregion Styles

interface NavbarProps {
  showSelectClient?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ showSelectClient = true }) => {

  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  const { formattedPermissions } = usePermissions();
  const { user } = useAuth();
  const { unseen: newVersionUnseen } = useSeenVersion(user?.email);

  const [searchIsActive, setSearchIsActive] = useState(false);
  const [openSidebar, setOpenSidebar] = useState(false);

  const externalProductIconPath = `${utils.getBackendBaseUrl(false)}/visual/LogotipoMinifiedWhite.svg`;

  /**
   * Toggle open / close sidebar
   */
  const toggleOpenSidebar = useCallback(() => {
    setOpenSidebar((value) => !value);
  }, []);

  /**
   * Return logo navbar based on screen width
   */
  const logoNavbar = useMemo(() => {

    const versionClass = width > 1280 ? "version version-desktop" : "version version-mobile";

    return (
      <>
        {width > 1280 ? (
          <ReactSVG src={LogoTopconCustomer} alt="Topcon Customer Logo" />
        ) : (
          <ExternalSvg svgName="LogotipoMinifiedColor" />
        )}
        <div className={versionClass}>V.{packageJson.version}</div>
      </>
    );

  }, [width]);

  return (
    <Styled.Container searchIsActive={searchIsActive}>
      <Styled.NavbarTop
        className="navbar-top"
        showSelectClient={showSelectClient}
      >
        <Badge
          variant="dot"
          invisible={!newVersionUnseen}
          sx={{ "& .MuiBadge-dot": { backgroundColor: "#f50057" } }}
        >
          <Menu onClick={toggleOpenSidebar} />
        </Badge>
        <SidebarMenuProvider>
          <SidebarDrawer
            primarycolor={theme.colors.primary}
            productIconPath={externalProductIconPath}
            productIconFallbackPath={LogotipoMinifiedWhite}
            productIconClass="product-sidebar-icon"
            open={openSidebar}
            onClose={toggleOpenSidebar}
            bodyChildren={<SidebarDrawerMenus />}
            footerChildren={<SidebarDrawerSecondaryMenus />}
          />
        </SidebarMenuProvider>
        <Styled.ProductIconContainer searchIsActive={searchIsActive}>
          {logoNavbar}
        </Styled.ProductIconContainer>
        <NavbarIcons
          searchIsActive={searchIsActive}
          setSearchIsActive={setSearchIsActive}
        />
      </Styled.NavbarTop>
      {showSelectClient && width < 1280 && (
        <Styled.NavbarBottom className="navbar-bottom">
          <SelectClient permissions={formattedPermissions ?? undefined} />
        </Styled.NavbarBottom>
      )}
    </Styled.Container>
  );
};

Navbar.defaultProps = {
  showSelectClient: true
};

export default React.memo(Navbar);
